import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { flushSync } from 'react-dom'
import i18n from 'i18next'
import { ArrowLeft, Loader2, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import { useLlmChatSessionsForProduct } from '@/shared/hooks/api/llmChat'
import { isArtemEarthEmail } from '@/shared/lib/isArtemEarthEmail'
import type {
  LexProduct,
  ProductInferredFact,
} from '@/shared/hooks/api/product'
import {
  PRODUCTS_QUERY_KEY,
  useGetProduct,
  useProductMetadataRefreshWorkflowLatest,
} from '@/shared/hooks/api/product'
import { useListProductSources } from '@/shared/hooks/api/productSource'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { EditProductDialog } from '@/features/product-catalog/components/EditProductDialog'
import { ProductComplianceStudiosPanel } from '@/features/product-catalog/components/ProductComplianceStudiosPanel'
import { ProductDeleteDialog } from '@/features/product-catalog/components/ProductDeleteDialog'
import { ProductInferredFactDeleteDialog } from '@/features/product-catalog/components/ProductInferredFactDeleteDialog'
import { ProductInferredFactEditDialog } from '@/features/product-catalog/components/ProductInferredFactEditDialog'
import { ProductInferredFactsCard } from '@/features/product-catalog/components/ProductInferredFactsCard'
import { ProductInitializationView } from '@/features/product-catalog/components/ProductInitializationView'
import { ProductLinkedChatsSection } from '@/features/product-catalog/components/ProductLinkedChatsSection'
import { ProductManufacturerCard } from '@/features/product-catalog/components/ProductManufacturerCard'
import { ProductSourcesPanel } from '@/features/product-catalog/components/ProductSourcesPanel'
import { useProductFileSourceUpload } from '@/features/product-catalog/hooks/useProductFileSourceUpload'
import { useInFlightProductSourceUploadIds } from '@/features/product-catalog/hooks/useInFlightProductSourceUploadIds'
import { getProductChatAvailability } from '@/features/product-catalog/utils/productChatAvailability'
import {
  getProductDetailPath,
  getProductSourceInspectSourceId,
} from '@/features/product-catalog/utils/productSourceInspectPath'
import { ProductSourceDetail } from './ProductSourceDetail'

const PRODUCT_CATALOG_NAMESPACES = ['productCatalog'] as const

function ProductDetailLoaded({
  product,
}: {
  product: LexProduct
}): ReactElement {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation('productCatalog')
  const { data: session } = useGetUserSession()
  const showAskLex = isArtemEarthEmail(session?.email)
  const { data: sources = [] } = useListProductSources(product.id)
  const { data: linkedChatsData } = useLlmChatSessionsForProduct(product.id)
  const { data: metadataRefreshWorkflow } =
    useProductMetadataRefreshWorkflowLatest(product.id, product.isInitialized)
  const { handleFileSelect, handleMultiFileSelect, uploadingSourceIds } =
    useProductFileSourceUpload(product.id)
  const inFlightUploadSourceIds = useInFlightProductSourceUploadIds(product.id)
  const activeUploadSourceIds = useMemo(() => {
    const merged = new Set(uploadingSourceIds)
    for (const sourceId of inFlightUploadSourceIds) {
      merged.add(sourceId)
    }
    return merged
  }, [uploadingSourceIds, inFlightUploadSourceIds])

  const [editProductDialogOpen, setEditProductDialogOpen] = useState(false)
  const [deleteProductDialogOpen, setDeleteProductDialogOpen] = useState(false)
  const [inferredFactDialogOpen, setInferredFactDialogOpen] = useState(false)
  const [inferredFactDeleteDialogOpen, setInferredFactDeleteDialogOpen] =
    useState(false)
  const [selectedInferredFact, setSelectedInferredFact] =
    useState<ProductInferredFact | null>(null)

  const isIngesting = useMemo(() => {
    const uploadFromServerPending = sources.some(
      (row) =>
        row.ingestionStatus === 'PROCESSING' ||
        (row.ingestionStatus === 'PENDING' && row.fileUploadStatus !== 'FAILED')
    )
    return activeUploadSourceIds.size > 0 || uploadFromServerPending
  }, [sources, activeUploadSourceIds])

  const allSourcesCompleted = useMemo(
    () =>
      sources.length > 0 &&
      sources.every((source) => source.ingestionStatus === 'COMPLETED'),
    [sources]
  )

  const showInit =
    !product.isInitialized && (isIngesting || allSourcesCompleted)

  const isDescriptionRefreshRunning =
    product.isInitialized && metadataRefreshWorkflow?.status === 'running'

  const hasDescriptionText =
    product.description != null && product.description.trim() !== ''

  const previousMetadataRefreshRef = useRef<{
    runId: string | null | undefined
    status: string | undefined
  } | null>(null)

  useEffect(() => {
    const runId = metadataRefreshWorkflow?.runId
    const status = metadataRefreshWorkflow?.status
    const previous = previousMetadataRefreshRef.current

    if (previous == null) {
      previousMetadataRefreshRef.current = { runId, status }
      return
    }

    const transitionedToCompleted =
      status === 'completed' &&
      runId != null &&
      (previous.status !== 'completed' || previous.runId !== runId)

    previousMetadataRefreshRef.current = { runId, status }

    if (transitionedToCompleted) {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, product.id],
      })
    }
  }, [
    metadataRefreshWorkflow?.runId,
    metadataRefreshWorkflow?.status,
    product.id,
    queryClient,
  ])

  const chatAvailability = useMemo(
    () => getProductChatAvailability({ product, sources }),
    [product, sources]
  )
  const hasDetailRows = chatAvailability.hasMetadata

  const pageTitle =
    product.name?.trim() !== '' && product.name != null
      ? product.name.trim()
      : t('fallbackProductTitle')

  const linkedChatCount = linkedChatsData?.sessions.length ?? 0
  const inferredFacts = product.inferredFactsJson?.items ?? []

  function onOpenInferredFactDialog(fact: ProductInferredFact) {
    setSelectedInferredFact(fact)
    setInferredFactDialogOpen(true)
  }

  function onInferredFactDialogOpenChange(open: boolean) {
    setInferredFactDialogOpen(open)
    if (!open) {
      setSelectedInferredFact(null)
    }
  }

  function onOpenInferredFactDeleteDialog(fact: ProductInferredFact) {
    setSelectedInferredFact(fact)
    setInferredFactDeleteDialogOpen(true)
  }

  function onInferredFactDeleteDialogOpenChange(open: boolean) {
    setInferredFactDeleteDialogOpen(open)
    if (!open) {
      setSelectedInferredFact(null)
    }
  }

  const detailsCard = (
    <Card className='h-fit'>
      <CardHeader className='flex flex-row flex-wrap items-start justify-between gap-2 space-y-0 pb-4'>
        <CardTitle className='text-base leading-tight'>
          {t('detail.detailsTitle')}
        </CardTitle>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          className='size-8 shrink-0 text-muted-foreground hover:text-foreground'
          aria-label={t('detail.editMetadataAriaLabel')}
          onClick={() => setEditProductDialogOpen(true)}
        >
          <Pencil className='size-4' aria-hidden />
        </Button>
      </CardHeader>
      <CardContent className='space-y-1 text-sm text-muted-foreground'>
        {product.name != null && product.name.trim() !== '' && (
          <p>
            <span className='font-medium text-foreground'>
              {t('detail.fields.name')}{' '}
            </span>
            {product.name}
          </p>
        )}
        {product.productModel != null && product.productModel.trim() !== '' && (
          <p>
            <span className='font-medium text-foreground'>
              {t('detail.fields.productModel')}{' '}
            </span>
            {product.productModel}
          </p>
        )}
        {product.yearOfManufacture != null && (
          <p>
            <span className='font-medium text-foreground'>
              {t('detail.fields.yearOfManufacture')}{' '}
            </span>
            {String(product.yearOfManufacture)}
          </p>
        )}
        {hasDescriptionText && (
          <div className='space-y-1 pt-1'>
            <p className='font-medium text-foreground'>
              {t('detail.fields.description')}
            </p>
            <CardDescription className='text-sm font-normal text-muted-foreground'>
              {product.description}
            </CardDescription>
            {isDescriptionRefreshRunning && (
              <p className='flex items-center gap-2 pt-1 text-sm text-muted-foreground'>
                <Loader2
                  className='size-4 shrink-0 animate-spin'
                  aria-hidden
                />
                {t('detail.updatingDescriptionFromSources')}
              </p>
            )}
          </div>
        )}
        {!hasDescriptionText && isDescriptionRefreshRunning && (
          <p className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='size-4 shrink-0 animate-spin' aria-hidden />
            {t('detail.updatingDescriptionFromSources')}
          </p>
        )}
        {!hasDetailRows && !isDescriptionRefreshRunning && (
          <p className='text-sm text-muted-foreground'>
            {t('detail.emptyDetails')}
          </p>
        )}
      </CardContent>
    </Card>
  )

  const chatsCard = (
    <ProductLinkedChatsSection
      productId={product.id}
      disableCreation={!chatAvailability.canStartProductChat}
      chatUnavailableReason={chatAvailability.chatUnavailableReason}
    />
  )
  const complianceStudiosCard = (
    <ProductComplianceStudiosPanel
      productId={product.id}
      disableCreation={!hasDetailRows}
    />
  )
  const sourcesCard = (
    <ProductSourcesPanel
      productId={product.id}
      sources={sources}
      uploadingSourceIds={activeUploadSourceIds}
      onUploadFile={handleFileSelect}
      onMultiFileUpload={handleMultiFileSelect}
    />
  )
  const inferredFactsCard = (
    <ProductInferredFactsCard
      inferredFacts={inferredFacts}
      onEditFact={onOpenInferredFactDialog}
      onDeleteFact={onOpenInferredFactDeleteDialog}
    />
  )

  return (
    <>
      <div className='space-y-4'>
        <div className='flex flex-wrap items-center justify-between gap-4'>
          <div className='flex min-w-0 flex-1 items-center gap-2'>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='size-8 shrink-0 text-muted-foreground hover:text-foreground'
              aria-label={t('detail.backToList')}
              asChild
            >
              <Link to='/product-catalog'>
                <ArrowLeft className='size-4' aria-hidden />
              </Link>
            </Button>
            <h1 className='truncate text-2xl font-bold tracking-tight'>
              {pageTitle}
            </h1>
          </div>
          <div className='flex shrink-0 flex-wrap items-center gap-2'>
            <Button
              type='button'
              variant='destructive'
              aria-label={t('detail.deleteProductAriaLabel', {
                title: pageTitle,
              })}
              onClick={() => setDeleteProductDialogOpen(true)}
            >
              <Trash2 className='size-4' aria-hidden />
              {t('common:actions.delete')}
            </Button>
          </div>
        </div>

        <div className='flex min-h-[320px] flex-1 flex-col'>
          <div className='flex min-h-0 flex-1 flex-col gap-4'>
            <div className='grid min-h-0 flex-1 gap-4 md:grid-cols-[360px_1fr]'>
              <div className='flex min-h-0 min-w-0 flex-col gap-4'>
                {sourcesCard}
                <ProductManufacturerCard productId={product.id} />
                {showInit ? (
                  <ProductInitializationView
                    productId={product.id}
                    isIngesting={isIngesting}
                  />
                ) : (
                  <>
                    {inferredFactsCard}
                    {detailsCard}
                  </>
                )}
              </div>
              <div className='flex min-h-[280px] min-w-0 flex-col gap-4'>
                {complianceStudiosCard}
                {showAskLex && chatsCard}
              </div>
            </div>
          </div>
        </div>
      </div>
      <EditProductDialog
        product={product}
        open={editProductDialogOpen}
        onOpenChange={setEditProductDialogOpen}
      />
      <ProductDeleteDialog
        open={deleteProductDialogOpen}
        onOpenChange={setDeleteProductDialogOpen}
        product={product}
        linkedChatCount={linkedChatCount}
        onDeleted={() => navigate('/product-catalog')}
      />
      <ProductInferredFactEditDialog
        productId={product.id}
        fact={selectedInferredFact}
        open={inferredFactDialogOpen}
        onOpenChange={onInferredFactDialogOpenChange}
      />
      <ProductInferredFactDeleteDialog
        productId={product.id}
        fact={selectedInferredFact}
        open={inferredFactDeleteDialogOpen}
        onOpenChange={onInferredFactDeleteDialogOpenChange}
      />
    </>
  )
}

export function ProductDetailPage() {
  const params = useParams<{ productId: string }>()
  const productId = params.productId!
  const location = useLocation()
  const navigate = useNavigate()
  const sourceId = getProductSourceInspectSourceId(location.pathname, productId)
  const [forceProductView, setForceProductView] = useState(false)
  const [retainedInspectSourceId, setRetainedInspectSourceId] = useState<
    string | undefined
  >()
  const showSourceDetail = sourceId != null && !forceProductView
  const inspectSourceId = sourceId ?? retainedInspectSourceId
  const { t } = useTranslation('productCatalog')
  const { data: product, isLoading, isError } = useGetProduct(productId)

  useEffect(() => {
    if (sourceId != null) {
      setRetainedInspectSourceId(sourceId)
    }
  }, [sourceId])

  useEffect(() => {
    if (sourceId == null) {
      setForceProductView(false)
    }
  }, [sourceId])

  useEffect(() => {
    if (sourceId != null || retainedInspectSourceId == null) {
      return
    }
    const frameId = window.requestAnimationFrame(() => {
      setRetainedInspectSourceId(undefined)
    })
    return () => window.cancelAnimationFrame(frameId)
  }, [sourceId, retainedInspectSourceId])

  const handleBackToProduct = useCallback(() => {
    flushSync(() => {
      setForceProductView(true)
    })
    navigate(getProductDetailPath(productId))
  }, [navigate, productId])

  useEffect(() => {
    void i18n.loadNamespaces([...PRODUCT_CATALOG_NAMESPACES])
  }, [])

  const header = (
    <Header fixed>
      <div className='ms-auto flex items-center space-x-4'>
        <ThemeSwitch />
        <ProfileDropdown />
      </div>
    </Header>
  )

  if (isLoading) {
    return (
      <>
        {header}
        <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='size-8 animate-spin text-muted-foreground' />
          </div>
        </Main>
      </>
    )
  }

  if (!product || isError) {
    return (
      <>
        {header}
        <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>
          <div className='space-y-4'>
            <div className='flex items-center gap-2'>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8 shrink-0 text-muted-foreground hover:text-foreground'
                aria-label={t('detail.backToList')}
                asChild
              >
                <Link to='/product-catalog'>
                  <ArrowLeft className='size-4' aria-hidden />
                </Link>
              </Button>
              <h1 className='text-2xl font-bold tracking-tight'>
                {t('detail.notFound')}
              </h1>
            </div>
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      {header}
      <Main className='flex flex-1 flex-col gap-4 sm:gap-6'>
        <div hidden={showSourceDetail}>
          <ProductDetailLoaded product={product} />
        </div>
        {inspectSourceId != null && (
          <div hidden={!showSourceDetail}>
            <ProductSourceDetail
              sourceId={inspectSourceId}
              active={showSourceDetail}
              onBackToProduct={handleBackToProduct}
            />
          </div>
        )}
      </Main>
    </>
  )
}
